import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Outreach.css';
import {
    getShortlistProfiles,
    removeProfileFromShortlist,
    subscribeToCandidateStatuses
} from '../../services/outreachService';

const LetterBadge = ({ name }) => {
    const letter = (name || 'U').slice(0, 1).toUpperCase();
    return (
        <div style={{
            width: '22px',
            height: '22px',
            borderRadius: '4px',
            background: 'var(--app-surface, rgba(86, 141, 237, 0.20))',
            color: 'var(--app-text, #FFFFFF)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: "var(--app-weight-strong, 900)",
            fontSize: '16px',
            lineHeight: '22px',
            textTransform: 'none',
            flexShrink: 0
        }}>
            {letter}
        </div>
    );
};

const OutreachCandidates = ({ campaign, isDraft, companyId }) => {
  useUiLanguage();
    const { t } = useTranslation('sourcing');
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);

    // Firebase state
    const [candidates, setCandidates] = useState([]);
    const [candidateStatuses, setCandidateStatuses] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // Fetch candidates from shortlist
    useEffect(() => {
        if (!companyId || !campaign?.shortlistId) {
            setIsLoading(false);
            return;
        }

        const fetchCandidates = async () => {
            setIsLoading(true);
            try {
                const profiles = await getShortlistProfiles(companyId, campaign.shortlistId);
                setCandidates(profiles || []);
            } catch (err) {
                console.error('Error fetching candidates:', err);
                setCandidates([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCandidates();
    }, [companyId, campaign?.shortlistId]);

    // Subscribe to candidate statuses for active/completed campaigns
    useEffect(() => {
        if (!companyId || !campaign?.id || isDraft) return;

        const unsubscribe = subscribeToCandidateStatuses(companyId, campaign.id, (statuses) => {
            if (statuses) setCandidateStatuses(statuses);
        });

        return () => unsubscribe();
    }, [companyId, campaign?.id, isDraft]);

    // Filter candidates by search term
    const filteredCandidates = candidates.filter(c => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const name = c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim();
        return (
            name.toLowerCase().includes(term) ||
            (c.company?.toLowerCase() || '').includes(term) ||
            (c.jobTitle?.toLowerCase() || '').includes(term)
        );
    });

    // Save/Restore scroll position
    useLayoutEffect(() => {
        const savedPosition = sessionStorage.getItem('outreachCandidatesScroll');
        if (savedPosition && containerRef.current) {
            containerRef.current.scrollTop = parseInt(savedPosition, 10);
        }

        const handleScroll = () => {
            if (containerRef.current) {
                sessionStorage.setItem('outreachCandidatesScroll', containerRef.current.scrollTop.toString());
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    // Navigate to the TalentSearch profile page
    const handleCandidateClick = (candidate) => {
        const name = candidate.fullName || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
        const profile = {
            metadata: {
                ...candidate, // Pass all fields (skills, pinecone_id, tags etc)
                user_uid: candidate.user_uid || candidate.id,
                name: name,
                username: name.split(' ')[0],
                fullUsername: name,
                // Prioritize raw experience string for the profile header (Role @ Company (Dates))
                //Fallback to composed string if only structured data exists
                experience: candidate.experience || (candidate.jobTitle && candidate.company ? `${candidate.jobTitle} @ ${candidate.company}` : candidate.jobTitle),
                company: candidate.company,
                email: candidate.email,
                linkedin: candidate.linkedin
            }
        };

        navigate('/sourcing/profile', {
            state: {
                profile: profile,
                searchState: {
                    searchText: '',
                    profiles: [],
                    lebanonDescription: '',
                    selectedUid: candidate.user_uid || candidate.id,
                },
                justRanOut: false,
                fromOutreach: true,
                campaign: campaign,
                tab: 'candidates'
            },
        });
    };

    const handleRemoveCandidate = async (e, candidate) => {
        e.stopPropagation();
        if (!window.confirm(t('outreach.confirmRemoveCandidate', 'Are you sure you want to remove this candidate from the shortlist?'))) return;

        try {
            const candidateId = candidate.id || candidate.user_uid;
            await removeProfileFromShortlist(companyId, campaign.shortlistId, candidateId);

            // Update local state
            setCandidates(prev => prev.filter(c => (c.id || c.user_uid) !== candidateId));
        } catch (err) {
            console.error('Error removing candidate:', err);
            alert(t('outreach.errors.removeCandidateFailed', 'Failed to remove candidate'));
        }
    };

    // Get status info for a candidate
    const getStatusInfo = (candidateId) => {
        const status = candidateStatuses[candidateId];
        if (!status) {
            return { status: isDraft ? 'ready' : 'scheduled', currentStep: 0 };
        }
        return status;
    };

    const getStatusBadge = (status, currentStep) => {
        let colorClass = '';
        let label = '';

        if (isDraft) {
            colorClass = 'status-active-seq';
            label = t('outreach.tabs.draft', 'Draft');
            return (
                <span className={`candidates-status-badge ${colorClass}`}>
                    <span className="dot"></span>
                    {uiText(label)}
                </span>
            );
        }

        switch (status) {
            case 'replied':
                colorClass = 'status-replied';
                label = t('outreach.status.replied', 'Replied');
                break;
            case 'completed':
                colorClass = 'status-completed-seq';
                label = t('outreach.status.sequenceComplete', 'Sequence Complete');
                break;
            case 'active':
                colorClass = 'status-active-seq';
                label = t('outreach.status.activeSequence', 'Active Sequence');
                break;
            case 'scheduled':
                colorClass = 'status-scheduled';
                label = t('outreach.status.scheduledLater', 'Scheduled Later');
                break;
            case 'bounced':
                colorClass = 'status-bounced';
                label = t('outreach.status.emailBounced', 'Email Bounced');
                break;
            default:
                colorClass = 'status-default';
                label = status || 'Pending';
        }

        return (
            <span className={`candidates-status-badge ${colorClass}`}>
                <span className="dot"></span>
                {uiText(label)}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="outreach-candidates-view" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <div style={{ textAlign: 'center' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px', color: '#568ded', marginBottom: '12px' }}></i>
                    <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>{t('common.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="outreach-candidates-view">
            <div className="search-bar-container" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)', fontSize: '14px', pointerEvents: 'none' }}></i>
                    <input
                        type="text"
                        placeholder={t('outreach.candidates.searchPlaceholder', 'Search By Candidate Name')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                        style={{ width: '100%', paddingLeft: '40px' }}
                    />
                </div>

                {isDraft && (
                    <button
                        className="add-candidates-btn"
                        onClick={() => navigate('/sourcing')}
                        style={{ '--light-control-fill': '#568ded',
                            background: '#568ded',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: 600,
                            fontSize: '14px'
                        }}
                      data-light-control={"filled"}>
                        <i className="fa-solid fa-plus"></i>
                        {t('outreach.candidates.addCandidates', 'Add Candidates')}
                    </button>
                )}
            </div>

            <div className="candidates-table-container" ref={containerRef}>
                <table className="candidates-table">
                    <thead>
                        <tr>
                            <th><i className="fa-solid fa-user"></i> {t('outreach.cols.candidateName', 'Candidate Name')}</th>
                            <th><i className="fa-solid fa-briefcase"></i> {t('outreach.cols.role', 'Role')}</th>
                            <th><i className="fa-regular fa-building"></i> {t('outreach.cols.company', 'Company')}</th>
                            <th><i className="fa-solid fa-circle-exclamation"></i> {t('outreach.cols.status', 'Status')}</th>
                            <th><i className="fa-solid fa-list-ol"></i> {t('outreach.cols.step', 'Step')}</th>
                            {isDraft && <th style={{ width: '50px' }}></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCandidates.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>
                                    {searchTerm
                                        ? t('outreach.candidates.noResults', 'No candidates match your search')
                                        : t('outreach.candidates.noCandidates', 'No candidates in this shortlist')
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredCandidates.map((candidate, index) => {
                                const name = candidate.fullName || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || uiText("Unknown");
                                const statusInfo = getStatusInfo(candidate.id || candidate.user_uid);

                                return (
                                    <tr key={candidate.id || candidate.user_uid || index}>
                                        <td className="font-bold white-text">
                                            <div
                                                onClick={() => handleCandidateClick(candidate)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    cursor: 'pointer',
                                                    padding: '4px 0'
                                                }}
                                                className="candidate-name-cell"
                                            >
                                                <LetterBadge name={name} />
                                                <span
                                                    style={{
                                                        transition: 'text-decoration 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                >
                                                    {name}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{candidate.jobTitle || '-'}</td>
                                        <td>{candidate.company || '-'}</td>
                                        <td>{getStatusBadge(statusInfo.status, statusInfo.currentStep)}</td>
                                        <td style={{ textAlign: 'center', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>
                                            {(() => {
                                                const emailNodes = (campaign?.nodes || []).filter(n => n.type === 'email');
                                                const totalSteps = emailNodes.length;
                                                const status = statusInfo.status;
                                                // Candidates who haven't started yet show 0
                                                // currentStep is 0-indexed node index; after step 0 is sent, show 1/X
                                                const completedSteps = (status === 'scheduled' || status === 'ready' || status === 'pending')
                                                    ? 0
                                                    : (statusInfo.currentStep !== undefined ? statusInfo.currentStep + 1 : 0);
                                                return `${Math.min(completedSteps, totalSteps)} / ${totalSteps}`;
                                            })()}
                                        </td>

                                        {isDraft && (
                                            <td>
                                                <button
                                                    onClick={(e) => handleRemoveCandidate(e, candidate)}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: 'var(--app-tone-e74c3c, #e74c3c)',
                                                        cursor: 'pointer',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        transition: 'background 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.background = 'rgba(231, 76, 60, 0.1)'}
                                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                  data-light-control={"quiet-danger"}>
                                                    <i className="fa-regular fa-trash-can"></i>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OutreachCandidates;
